'use client';

import {
	useMutation,
	UseMutationOptions,
	UseMutationResult,
} from '@tanstack/react-query';
import { ServerActionResult } from '@/shared/types/server-action.type';
import { useGlobalDialogStore } from '@/shared/stores/useGlobalDialogStore';
import { errorRegistry } from '@/core/error';
import { withAuth } from '../utils/withAuth';

// Custom error class to carry the error code
class SafeMutationError extends Error {
	constructor(
		message: string,
		public readonly code: string
	) {
		super(message);
		this.name = 'SafeMutationError';
	}
}

function isGlobalError(error: Error | null): boolean {
	return (
		error instanceof SafeMutationError &&
		errorRegistry[error.code] !== undefined
	);
}

interface UseSafeMutationOptions<
	TData,
	TVariables,
	TContext = unknown,
> extends Omit<
	UseMutationOptions<TData, Error, TVariables, TContext>,
	'mutationFn'
> {
	action: (variables: TVariables) => Promise<ServerActionResult<TData>>;
}

export function useSafeMutation<TData, TVariables = void, TContext = unknown>(
	options: UseSafeMutationOptions<TData, TVariables, TContext>
): UseMutationResult<TData, Error, TVariables, TContext> {
	const { action, onError, ...mutationOptions } = options;
	const openDialog = useGlobalDialogStore((state) => state.openDialog);

	const mutation = useMutation({
		...mutationOptions,
		mutationFn: async (variables: TVariables) => {
			const result = await withAuth(action)(variables);

			if (!result.success) {
				const errorCode = result.error?.code ?? 'GENERIC_ERROR';
				const errorMessage =
					result.error?.message ??
					'Something went wrong! Please try again.';

				const dialogConfig = errorRegistry[errorCode];
				if (dialogConfig) {
					openDialog(dialogConfig);
				}
				throw new SafeMutationError(errorMessage, errorCode);
			}

			return result.data as TData;
		},
		// Intercept onError to only call user's callback for non-global errors
		onError: (error, variables, context, mutation) => {
			if (!isGlobalError(error)) {
				onError?.(error, variables, context, mutation);
			}
		},
	});

	// Filter out global errors - component cannot see them
	const globalError = isGlobalError(mutation.error);
	return {
		...mutation,
		error: globalError ? null : mutation.error,
		isError: globalError ? false : mutation.isError,
	} as UseMutationResult<TData, Error, TVariables, TContext>;
}
